import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from '@app/constants/queryKeys';
import axios, { AxiosError, AxiosResponse } from 'axios';
import {
  ComposeCalldataResponse,
  GetProtocolQuotaResponse,
  GetProtocolQuotesPayload,
  PriorityStep
} from '@app/hooks/router-intent/types';
import React, { useEffect, useMemo } from 'react';
import { useRouterIntentState } from '@app/state/routerIntentStore';
import { useAddress, useChainId } from '@thirdweb-dev/react';
import { Base, Chain, Mode } from '@thirdweb-dev/chains';
import { parseUnits } from 'ethers/lib/utils';
import { nearestUsableTick, TickMath } from '@cryptoalgebra/kim-sdk';
import { useAsyncFn } from 'react-use';
import { defineChain } from 'thirdweb/chains';
import {
  createThirdwebClient,
  prepareTransaction,
  sendTransaction,
  waitForReceipt
} from 'thirdweb';
import { envConfig, useConfig } from '@app/config';
import { useAccount } from '@app/hooks/thirdweb/useAccount';
import { Account } from 'node_modules/thirdweb/dist/types/wallets/interfaces/wallet';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';

const client = createThirdwebClient({
  clientId: envConfig.thirdwebClientId!
});

async function fetcher(payload: GetProtocolQuotesPayload | undefined) {
  if (!payload) {
    return null;
  }

  try {
    const { data } = await axios.post<
      GetProtocolQuotesPayload,
      AxiosResponse<GetProtocolQuotaResponse>
    >(
      `https://api.pod.routerintents.com/router-intent/protocol/get-protocol-quotes`,
      payload
    );

    return data;
  } catch (e) {
    return (e as AxiosError)?.response?.data as GetProtocolQuotaResponse;
  }
}

async function composeCalldata(payload: GetProtocolQuotaResponse['PayLoad']) {
  try {
    const { data } = await axios.post<
      GetProtocolQuotaResponse['PayLoad'],
      AxiosResponse<ComposeCalldataResponse>
    >(
      `https://api.pod.routerintents.com/router-intent/adapter/compose-adapter-calldata`,
      payload
    );

    return data;
  } catch (e) {
    return (e as AxiosError)?.response?.data as ComposeCalldataResponse;
  }
}

async function executePrioritySteps(
  prioritySteps: PriorityStep[],
  sourceNetwork: Chain,
  account: Account
) {
  for (const step of prioritySteps) {
    const transaction = prepareTransaction({
      to: step.contractAddress,
      data: step.data as `0x${string}`,
      value: BigInt(step.value),
      chain: defineChain(sourceNetwork!),
      client
    });

    const txResult = await sendTransaction({
      transaction,
      account
    });

    const receipt = await waitForReceipt(txResult);

    return receipt;
  }
}

// todo - get from selected pool
const tickSpacing = 60;

export function useDepositLiquidity() {
  const address = useAddress();

  const account = useAccount();
  const config = useConfig();
  const chainId = useChainId();

  const {
    sourceNetwork,
    sourceToken,
    sourceAmount,
    targetPool,
    targetChainId,
    actions: { setTargetChainId }
  } = useRouterIntentState();

  useEffect(() => {
    if (chainId === Mode.chainId || chainId === Base.chainId) {
      setTargetChainId(chainId);
    }
  }, [chainId, setTargetChainId]);

  const quotaPayload = useMemo(() => {
    if (
      !sourceAmount ||
      !sourceNetwork ||
      !sourceToken ||
      !targetPool ||
      !address ||
      !targetChainId
    ) {
      return;
    }

    return {
      ReceiverAddress: address,
      SourceTokens: [
        {
          chainId: sourceNetwork.chainId.toString(),
          address: sourceToken.contractAddress,
          name: sourceToken.name,
          symbol: sourceToken.symbol,
          decimals: sourceToken.decimals
        }
      ],
      DestinationTokens: [
        {
          chainId: targetChainId.toString(),
          address: targetPool.token0.id,
          name: targetPool.token0.name,
          symbol: targetPool.token0.symbol,
          decimals: +targetPool.token0.decimals
        },
        {
          chainId: targetChainId.toString(),
          address: targetPool.token1.id,
          name: targetPool.token1.name,
          symbol: targetPool.token1.symbol,
          decimals: +targetPool.token1.decimals
        }
      ],
      Amount: [parseUnits(sourceAmount, sourceToken.decimals).toString()],
      SourceChainId: sourceNetwork.chainId,
      Protocol: [
        {
          protocolId: 'kim',
          chainId: targetChainId.toString(),
          action: 'deposit_v4',
          poolId: '',
          data: {
            lowerTick: nearestUsableTick(
              TickMath.MIN_TICK,
              tickSpacing
            ).toString(), //  TickMath.MIN_TICK.toString(), //  '-196500',
            upperTick: nearestUsableTick(
              TickMath.MAX_TICK,
              tickSpacing
            ).toString() // TickMath.MAX_TICK.toString() // '-195000'
          }
        }
      ]
    } satisfies GetProtocolQuotesPayload;
  }, [
    sourceAmount,
    sourceNetwork,
    sourceToken,
    targetPool,
    address,
    targetChainId
  ]);

  const { data, isLoading } = useQuery(
    [QueryKeys.GET_PROTOCOL_QUOTES, quotaPayload],
    () => fetcher(quotaPayload),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5000
    }
  );

  const [{ value, loading: preparingToDeposit }, depositLiquidity] =
    useAsyncFn(async () => {
      if (!data || !account || !sourceNetwork) {
        return;
      }

      try {
        const callDataParams = await composeCalldata(data.PayLoad);

        if (callDataParams.PayLoad.prioritySteps.length) {
          await executePrioritySteps(
            callDataParams.PayLoad.prioritySteps,
            sourceNetwork,
            account
          );
        }

        const transaction = prepareTransaction({
          to: callDataParams.PayLoad.to,
          data: callDataParams.PayLoad.calldata as `0x${string}`,
          value: BigInt(callDataParams.PayLoad.value),
          chain: defineChain(sourceNetwork!),
          client
        });

        const txResult = await sendTransaction({
          transaction,
          account
        });

        const receipt = await waitForReceipt(txResult);

        if (receipt) {
          showSuccessToast(
            'Transaction successful',
            <ExternalLink txHash={receipt.transactionHash} />
          );
        }

        return receipt;
      } catch (e) {
        debugger;
        console.error(e);
        showErrorToast('Transaction failed');
      }
    }, [data, sourceNetwork, client]);

  return {
    isLoading: isLoading || preparingToDeposit,
    errors: data?.Errors,
    isAvailable: !!data?.PayLoad,
    depositLiquidity,
    details: data,
    txResult: value
  };
}
